import { HTMLAttributes } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { useParams } from 'react-router-dom'

import { Skeleton } from '@/components/Skeleton'
import useSearchParams from '@/hooks/useSearchParams'
import { useLoadAbandonmentByResourceId } from '@/queries/form-analytics/useLoadAbandonmentByResourceId'
import { SimpleTable } from '@/ui/atoms/Table/SimpleTable'
import cn from '@/utils/tailwind/cn'

import { formatAvgTime } from '../../UtilsFormsAnalytics/formatAvgTime'
import { getDateRangeByPeriod } from '../../UtilsFormsAnalytics/getDateRangeByPeriod'
import { FormsAnalyticsCard } from '../FormsAnalyticsCard'
import { MOCK_TOP_DROPOFF_DATA } from './factory'

export const TopDropoff = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  const { resourceId } = useParams<{ resourceId: string }>()
  const searchParams = useSearchParams()

  const period = searchParams.get('period') || 'month'
  const startDate = searchParams.get('start_date')
  const endDate = searchParams.get('end_date')

  const { created_before, created_after } = getDateRangeByPeriod(
    period,
    startDate,
    endDate
  )

  const { data, isLoading } = useLoadAbandonmentByResourceId({
    resource_uid: resourceId,
    resource_type: 'form',
    created_after,
    created_before,
  })

  const dropoffData = isLoading
    ? MOCK_TOP_DROPOFF_DATA()
    : data?.filter((dropoff) => dropoff.name)

  return (
    <FormsAnalyticsCard.Root className={cn('!mt-4', className)} {...props}>
      <FormsAnalyticsCard.Title>Top Field Abandonment</FormsAnalyticsCard.Title>
      <SimpleTable.Root className="mt-2">
        <SimpleTable.Header>
          <SimpleTable.Row>
            <SimpleTable.Head>Field </SimpleTable.Head>
            <SimpleTable.Head>Drop-offs</SimpleTable.Head>
            <SimpleTable.Head>Drop-off Rate</SimpleTable.Head>
            <SimpleTable.Head>Avg. Time Spent</SimpleTable.Head>
          </SimpleTable.Row>
        </SimpleTable.Header>
        <SimpleTable.Body>
          {!dropoffData?.length ? (
            <SimpleTable.Row>
              <SimpleTable.Cell colSpan={4}>
                <span className="block w-full text-center text-gray-400">
                  No data
                </span>
              </SimpleTable.Cell>
            </SimpleTable.Row>
          ) : null}
          {dropoffData?.map((dropoff) => (
            <Fragment key={dropoff.question_id}>
              {isLoading ? (
                <SimpleTable.Row>
                  <SimpleTable.Cell className="py-0 !pt-1.5" colSpan={4}>
                    <Skeleton {...{ isLoading }} className="!h-[52px]">
                      <i className="h-4 w-full" />
                    </Skeleton>
                  </SimpleTable.Cell>
                </SimpleTable.Row>
              ) : (
                <SimpleTable.Row key={dropoff.question_id}>
                  <SimpleTable.Cell>{dropoff.name}</SimpleTable.Cell>
                  <SimpleTable.Cell>
                    {Math.ceil(Number(dropoff.drop_off_count)).toLocaleString(
                      'en-US'
                    )}
                  </SimpleTable.Cell>
                  <SimpleTable.Cell>
                    {dropoff.drop_off_rate.toLocaleString('en-US', {
                      style: 'percent',
                      minimumFractionDigits: 0,
                    })}
                  </SimpleTable.Cell>
                  <SimpleTable.Cell>
                    {formatAvgTime(dropoff.avg_time)}
                  </SimpleTable.Cell>
                </SimpleTable.Row>
              )}
            </Fragment>
          ))}
        </SimpleTable.Body>
      </SimpleTable.Root>
    </FormsAnalyticsCard.Root>
  )
}
